/*
 * WorkAndBuhin.vue の作業リセット、おすすめ整備リセット、整備なし・部品なし確定状態を切り出した処理。
 */
import { STATUS_SELECT_WORK_BUHIN } from "@/model/StatusSelectWorkBuhin";
import { toArray } from "@/components/work-buhin/WorkBuhinAdapter";

export interface WorkBuhinResetState {
  isResetSeibi: boolean;
  listSelectedBuhin: any[];
  listManualBuhin: any[];
  listManualBuhinFiltered: any[];
  disabledBtnConfirm: boolean;
  status: number;
}

export interface WorkResetApplyEffectState {
  isResetSeibi: boolean;
  listSelectedBuhin: any[];
  listManualBuhin: any[];
  listManualBuhinFiltered: any[];
  disabledBtnConfirm: boolean;
  status: number;
  shouldSetBuhinToWork: boolean;
}

export interface WorkResetCommitApplyEffectState {
  isResetSeibi: boolean;
  listSelectedBuhin: any[];
  listManualBuhin: any[];
  listManualBuhinFiltered: any[];
  disabledBtnConfirm: boolean;
  status: number;
  shouldSetBuhinToWork: boolean;
}

export interface WorkBuhinNoSeibiState {
  listSelectedBuhin: any[];
  listManualBuhin: any[];
  disabledBtnConfirm: boolean;
  disabledBtnNoBuhin: boolean;
  osusumeBuhinList: any[];
  activeCateBuhins: any[];
  buhinContent: any[];
  activeBuhinShubetsuId: number;
  activeBuhinCateIndex: number;
}

export interface NoSeibiApplyEffectState {
  listSelectedBuhin: any[];
  listManualBuhin: any[];
  disabledBtnConfirm: boolean;
  disabledBtnNoBuhin: boolean;
  osusumeBuhinList: any[];
  activeCateBuhins: any[];
  buhinContent: any[];
  activeBuhinShubetsuId: number;
  activeBuhinCateIndex: number;
  shouldFilterManualBuhin: boolean;
  shouldMapBuhin: boolean;
}

export interface WorkBuhinNoBuhinConfirmState {
  isBuhin: boolean;
  isResetSeibi: boolean;
  buhinContent: any[];
  listSelectedBuhin: any[];
  listManualBuhin: any[];
  disabledBtnConfirm: boolean;
  isKouchinChange: boolean;
  shouldClearDeletedManualBuhinList: boolean;
  status: number;
}

export interface BuhinConfirmActionState {
  isBuhin: boolean;
  isResetSeibi: boolean;
  status: number;
}

export interface BuhinConfirmAfterSaveActionState {
  shouldShowMsgConfirm: boolean;
  shouldResetKouchinChange: boolean;
  isKouchinChange: boolean;
}

export type BuhinConfirmAfterSaveApplyEffectState =
  BuhinConfirmAfterSaveActionState;

export interface NoBuhinConfirmAfterActionState {
  shouldFilterManualBuhin: boolean;
  status: number;
  shouldShowMsgConfirm: boolean;
  shouldClearDeletedManualBuhinList: boolean;
  tmpDeletedManualBuhinList: any[];
}

export type NoBuhinConfirmAfterApplyEffectState =
  NoBuhinConfirmAfterActionState;

/*
 * buildWorkResetState を WorkAndBuhin.vue から分離し、
 * reset と部品なし確定 を親側置換や単体テストで固定するため。
 */
export function buildWorkResetState(status: number): WorkBuhinResetState {
  return {
    isResetSeibi: true,
    listSelectedBuhin: [],
    listManualBuhin: [],
    listManualBuhinFiltered: [],
    disabledBtnConfirm: true,
    status
  };
}

/*
 * buildNormalWorkResetState を WorkAndBuhin.vue から分離し、
 * reset と部品なし確定 を親側置換や単体テストで固定するため。
 */
export function buildNormalWorkResetState(): WorkBuhinResetState {
  return buildWorkResetState(STATUS_SELECT_WORK_BUHIN.Initial);
}

/*
 * buildRecommendWorkResetState を WorkAndBuhin.vue から分離し、
 * reset と部品なし確定 を親側置換や単体テストで固定するため。
 */
export function buildRecommendWorkResetState(): WorkBuhinResetState {
  return buildWorkResetState(STATUS_SELECT_WORK_BUHIN.delete);
}

/*
 * buildWorkResetApplyEffectState を WorkAndBuhin.vue から分離し、
 * 通常作業/おすすめ整備 reset 時に component が反映する選択状態、保存 status、confirm button の副作用条件を固定するため。
 */
export function buildWorkResetApplyEffectState(
  resetState: WorkBuhinResetState
): WorkResetApplyEffectState {
  return {
    isResetSeibi: resetState.isResetSeibi,
    listSelectedBuhin: resetState.listSelectedBuhin,
    listManualBuhin: resetState.listManualBuhin,
    listManualBuhinFiltered: resetState.listManualBuhinFiltered,
    disabledBtnConfirm: resetState.disabledBtnConfirm,
    status: resetState.status,
    shouldSetBuhinToWork: true
  };
}

/*
 * buildWorkResetCommitApplyEffectState を WorkAndBuhin.vue から分離し、
 * 通常作業/おすすめ整備 reset 時に component が反映する state と保存呼び出し条件を固定するため。
 */
export function buildWorkResetCommitApplyEffectState(
  effectState: WorkResetApplyEffectState
): WorkResetCommitApplyEffectState {
  return {
    isResetSeibi: effectState.isResetSeibi,
    listSelectedBuhin: effectState.listSelectedBuhin,
    listManualBuhin: effectState.listManualBuhin,
    listManualBuhinFiltered: effectState.listManualBuhinFiltered,
    disabledBtnConfirm: effectState.disabledBtnConfirm,
    status: effectState.status,
    shouldSetBuhinToWork: effectState.shouldSetBuhinToWork
  };
}

/*
 * buildNoSeibiState を WorkAndBuhin.vue から分離し、
 * reset と部品なし確定 を親側置換や単体テストで固定するため。
 */
export function buildNoSeibiState(): WorkBuhinNoSeibiState {
  return {
    listSelectedBuhin: [],
    listManualBuhin: [],
    disabledBtnConfirm: true,
    disabledBtnNoBuhin: true,
    osusumeBuhinList: [],
    activeCateBuhins: [],
    buhinContent: [],
    activeBuhinShubetsuId: 0,
    activeBuhinCateIndex: -1
  };
}

/*
 * buildNoSeibiApplyEffectState を WorkAndBuhin.vue から分離し、
 * 整備なし選択時に component が反映する部品選択、button、棚表示 reset と再描画条件を固定するため。
 */
export function buildNoSeibiApplyEffectState(
  noSeibiState: WorkBuhinNoSeibiState
): NoSeibiApplyEffectState {
  return {
    listSelectedBuhin: noSeibiState.listSelectedBuhin,
    listManualBuhin: noSeibiState.listManualBuhin,
    disabledBtnConfirm: noSeibiState.disabledBtnConfirm,
    disabledBtnNoBuhin: noSeibiState.disabledBtnNoBuhin,
    osusumeBuhinList: noSeibiState.osusumeBuhinList,
    activeCateBuhins: noSeibiState.activeCateBuhins,
    buhinContent: noSeibiState.buhinContent,
    activeBuhinShubetsuId: noSeibiState.activeBuhinShubetsuId,
    activeBuhinCateIndex: noSeibiState.activeBuhinCateIndex,
    shouldFilterManualBuhin: true,
    shouldMapBuhin: true
  };
}

/*
 * buildBuhinConfirmActionState を WorkAndBuhin.vue から分離し、
 * 部品あり確定の保存前 state と setBuhinToWork status を固定するため。
 */
export function buildBuhinConfirmActionState(): BuhinConfirmActionState {
  return {
    isBuhin: true,
    isResetSeibi: false,
    status: STATUS_SELECT_WORK_BUHIN.Done
  };
}

/*
 * buildBuhinConfirmAfterSaveActionState を WorkAndBuhin.vue から分離し、
 * 部品あり確定の保存後 message 表示と工賃変更 reset 条件を固定するため。
 */
export function buildBuhinConfirmAfterSaveActionState(params: {
  doEmit: boolean;
  isDialog: boolean;
}): BuhinConfirmAfterSaveActionState {
  return {
    shouldShowMsgConfirm: params.doEmit && params.isDialog,
    shouldResetKouchinChange: true,
    isKouchinChange: false
  };
}

/*
 * buildBuhinConfirmAfterSaveApplyEffectState を WorkAndBuhin.vue から分離し、
 * 部品あり確定後に component が反映する message 表示と工賃変更 reset 条件を固定するため。
 */
export function buildBuhinConfirmAfterSaveApplyEffectState(
  actionState: BuhinConfirmAfterSaveActionState
): BuhinConfirmAfterSaveApplyEffectState {
  return {
    shouldShowMsgConfirm: actionState.shouldShowMsgConfirm,
    shouldResetKouchinChange: actionState.shouldResetKouchinChange,
    isKouchinChange: actionState.isKouchinChange
  };
}

/*
 * buildNoBuhinConfirmState を WorkAndBuhin.vue から分離し、
 * reset と部品なし確定 を親側置換や単体テストで固定するため。
 */
export function buildNoBuhinConfirmState(params: {
  buhinContent: any[];
  listManualBuhin: any[];
  isDialog: boolean;
}): WorkBuhinNoBuhinConfirmState {
  return {
    isBuhin: false,
    isResetSeibi: false,
    buhinContent: toArray<any>(params.buhinContent).map((bunrui: any) => ({
      ...bunrui,
      items: toArray<any>(bunrui && bunrui.items).map((buhin: any) => ({
        ...buhin,
        quantity: 0
      }))
    })),
    listSelectedBuhin: [],
    listManualBuhin: toArray<any>(params.listManualBuhin).map((buhin: any) => ({
      ...buhin,
      quantity: 0
    })),
    disabledBtnConfirm: true,
    isKouchinChange: false,
    shouldClearDeletedManualBuhinList: params.isDialog,
    status: STATUS_SELECT_WORK_BUHIN.Done
  };
}

/*
 * buildNoBuhinConfirmAfterActionState を WorkAndBuhin.vue から分離し、
 * 部品なし確定後の filter、保存、message、削除一時リスト clear 条件を固定するため。
 */
export function buildNoBuhinConfirmAfterActionState(
  noBuhinState: WorkBuhinNoBuhinConfirmState
): NoBuhinConfirmAfterActionState {
  return {
    shouldFilterManualBuhin: true,
    status: noBuhinState.status,
    shouldShowMsgConfirm: noBuhinState.shouldClearDeletedManualBuhinList,
    shouldClearDeletedManualBuhinList: noBuhinState.shouldClearDeletedManualBuhinList,
    tmpDeletedManualBuhinList: []
  };
}

/*
 * buildNoBuhinConfirmAfterApplyEffectState を WorkAndBuhin.vue から分離し、
 * 部品なし確定後に component が反映する filter、保存、message、削除一時リスト clear 条件を固定するため。
 */
export function buildNoBuhinConfirmAfterApplyEffectState(
  actionState: NoBuhinConfirmAfterActionState
): NoBuhinConfirmAfterApplyEffectState {
  return {
    shouldFilterManualBuhin: actionState.shouldFilterManualBuhin,
    status: actionState.status,
    shouldShowMsgConfirm: actionState.shouldShowMsgConfirm,
    shouldClearDeletedManualBuhinList: actionState.shouldClearDeletedManualBuhinList,
    tmpDeletedManualBuhinList: actionState.tmpDeletedManualBuhinList
  };
}
